import { SimpleGrid, Title } from '@mantine/core';
import { noop } from 'lodash/fp';
import React, { useEffect, useRef } from 'react';

import { ProductTypeEnum, useFeatureFlags } from '@portals/api/partners';
import { isPartnerFeatureOn } from '@portals/framework';
import {
  BoxCogWrench,
  BoxOpen,
  CloudArrowDown,
  CogWrench,
  DocumentMedal,
} from '@portals/framework/icons';

import { ProductTypeCard } from './ProductTypeCard';
import { useProductWizard } from '../use-product-wizard';

export function ProductTypeStep() {
  const { onNext, setContextData } = useProductWizard();
  const shouldCallOnNext = useRef(false);
  const featureFlags = useFeatureFlags();

  const disableAllProductTypes = !isPartnerFeatureOn(
    featureFlags,
    'enable_all_product_types'
  );

  const onSelectProductType = (productType: ProductTypeEnum) => {
    shouldCallOnNext.current = true;

    setContextData((prev) => ({
      ...prev,
      product: {
        ...prev.product,
        product_type: productType,
      },
    }));
  };

  useEffect(() => {
    // We need to invoke `onNext` only after the `contextData` was updated with the selected `product_type`
    if (shouldCallOnNext.current) {
      shouldCallOnNext.current = false;
      onNext();
    }
  }, [onNext]);

  return (
    <>
      <Title order={2} weight={600} mb={56} align="center">
        What product would you like to create?
      </Title>

      <SimpleGrid cols={5} spacing="xl" sx={{ placeContent: 'center' }}>
        <ProductTypeCard
          title="Physical Product"
          description="Offer any type of hardware and peripheral equipment, in any type of business model"
          icon={<BoxOpen />}
          disabled={false}
          onClick={() => onSelectProductType(ProductTypeEnum.Physical)}
        />
        <ProductTypeCard
          title="Digital Product"
          description="Sell a variety of digital products, such as software license keys, or premium features"
          icon={<CloudArrowDown />}
          disabled={false}
          onClick={() => onSelectProductType(ProductTypeEnum.Digital)}
        />
        <ProductTypeCard
          title="Service"
          description="Sell your customers any service, such as a long-term support contract, or a one-time visit"
          icon={<CogWrench />}
          disabled={disableAllProductTypes}
          onClick={noop}
        />
        <ProductTypeCard
          title="Warranty"
          description="Is your customer’s warranty about to expire? easily offer an extended, customized warranty"
          icon={<DocumentMedal />}
          disabled={disableAllProductTypes}
          onClick={noop}
        />
        <ProductTypeCard
          title="Bundle"
          description="Sell a bundle of a variety of products, of any type, and at any business model"
          icon={<BoxCogWrench />}
          disabled={disableAllProductTypes}
          onClick={noop}
        />
      </SimpleGrid>
    </>
  );
}
